import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  InjectionToken,
  input,
  InputSignal,
  Signal,
  signal,
  ValueProvider,
  viewChild,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faCrop, faFileUpload, faImage, faTrash, faUpload } from '@fortawesome/pro-light-svg-icons';
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ImageCropDialogComponent } from './image-crop-dialog.component';
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import {
  getBase64ImageFormat,
  IMAGE_FORMATS,
  ImageFormat,
  parseBase64ToImage,
  removeImagePrefixFromBase64
} from '../table-implementation/functions/base64-image-parse';

export type OnImageInputChange = (image: string | null) => void;
export type OnImageInputTouched = (isTouche: boolean) => void;
export const IMAGE_UPLOAD_TRANSLATION_SCOPE: InjectionToken<string | string[]> = new InjectionToken<string | string[]>("image-upload-translation-scope");
export function provideImageUploadTranslationScope (value: string | string[]): ValueProvider {
  return {
    provide: IMAGE_UPLOAD_TRANSLATION_SCOPE,
    useValue: value
  };
}
@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IconModule,
    MatButtonModule,
    MatDialogModule,
    TranslationPipeModule
  ],
  providers: [
    provideTranslationScope("shared/image-upload"),
    { provide: MatFormFieldControl, useExisting: ImageUploadComponent }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-upload.component.html',
  styleUrl: './image-upload.component.scss'
})
export class ImageUploadComponent implements ControlValueAccessor {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  translationScope: string | string[] = inject(IMAGE_UPLOAD_TRANSLATION_SCOPE, { optional: true }) ?? "shared/image-upload";

  public readonly aspectRatio: InputSignal<number> = input<number>(1 / 1);

  public readonly outputFormat: InputSignal<ImageFormat> = input<ImageFormat>('png');

  private readonly _fileInput: Signal<ElementRef<HTMLInputElement>> = viewChild.required("fileInput");

  private readonly _ngControl: NgControl | null = inject(NgControl, { self: true, optional: true });

  private _isTouched: boolean = false;

  private _onChangeCallback: OnImageInputChange | undefined;

  private _onTouchedCallback: OnImageInputTouched | undefined;

  private _isDisabled: boolean = false;

  protected dragOverActive: WritableSignal<boolean> = signal<boolean>(false);


  private _currentImageFormat: WritableSignal<ImageFormat> = signal(IMAGE_FORMATS.png);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected preview: Signal<string | undefined> = computed(() => {
    const value: string | null = this.value();
    const imageFormat: ImageFormat = this._currentImageFormat();

    return value ? parseBase64ToImage(value, imageFormat) : undefined;
  });

  protected value: WritableSignal<string | null> = signal<string | null>(null);

  private readonly _dialog: MatDialog = inject(MatDialog);

  constructor () {
    this._faIconLibrary.addIcons(faTrash, faUpload, faImage, faFileUpload, faCrop);

    if (this._ngControl)
      this._ngControl.valueAccessor = this;
  }

  public registerOnChange (callback: OnImageInputChange | undefined): void {
    this._onChangeCallback = callback;
  }

  public registerOnTouched (callback: OnImageInputTouched | undefined): void {
    this._onTouchedCallback = callback;
  }

  public setDisabledState? (isDisabled: boolean): void {
    this._isDisabled = isDisabled;
  }

  public writeValue (value: string | null): void {
    this.setValue(value, false);
  }

  onFileSelected (selectEvent: Readonly<Event>): void {
    const inputEl: HTMLInputElement = selectEvent.target as HTMLInputElement;
    const file: File | undefined = inputEl.files?.[ 0 ];

    if (!file) return;

    const imageFormat: ImageFormat | undefined = getBase64ImageFormat(file.type);

    if (!imageFormat) {
      inputEl.value = '';

      return;
    }

    this.readFileAsBase64(file);
    inputEl.value = '';
    this.markAsTouched();
  }

  private readFileAsBase64 (file: Readonly<File>): void {
    const reader: FileReader = new FileReader();

    reader.onload = (): void => {
      const base64: string = reader.result as string;

      this.setValue(base64);
    };
    reader.readAsDataURL(file);
  }

  triggerFileDialog (): void {
    if (this._isDisabled) return;

    this._fileInput().nativeElement.click();
  }

  clearImage (): void {
    if (this._isDisabled) return;

    this.setValue(null);
    this.markAsTouched();
  }

  onDragEnter (enterEvent: Readonly<DragEvent>): void {
    if (this._isDisabled) return;

    enterEvent.preventDefault();

    if (!this.dragOverActive())
      this.dragOverActive.set(true);
  }

  protected onDragOver (overEvent: Readonly<DragEvent>): void {
    if (this._isDisabled) return;

    overEvent.preventDefault();

    if (!this.dragOverActive()) this.dragOverActive.set(true);
  }

  protected onDragLeave (leaveEvent: Readonly<DragEvent>): void {
    if (this._isDisabled) return;

    leaveEvent.preventDefault();
  }

  protected onDrop (dropEvent: Readonly<DragEvent>): void {
    if (this._isDisabled) return;

    dropEvent.preventDefault();
    this.dragOverActive.set(false);

    const file: File | undefined = dropEvent.dataTransfer?.files[ 0 ];

    if (file) {
      this.readFileAsBase64(file);
      this.markAsTouched();
    }
  }

  enterCropMode (): void {
    if (this._isDisabled) return;

    const currentValue: string | null = this.value();

    if (!currentValue) return;

    const ref: MatDialogRef<ImageCropDialogComponent> = this._dialog.open(ImageCropDialogComponent, {
      data: {
        imageBase64: currentValue,
        aspectRatio: this.aspectRatio(),
        format: this.outputFormat()
      },
      panelClass: 'image-crop-dialog-panel'
    });

    ref.afterClosed().subscribe((resultBase64: string | null) => {
      if (resultBase64) {
        this.setValue(resultBase64);
        this.markAsTouched();
      }
    });
  }

  private setValue (value: string | null, shouldEmitEvent: boolean = true): void {
    let computedValue: string | null = value;

    if (value) {
      const imageFormat: ImageFormat | undefined = getBase64ImageFormat(value);

      if (imageFormat) {
        computedValue = removeImagePrefixFromBase64(value, imageFormat);
        this._currentImageFormat.set(imageFormat);
      }
    }

    this.value.set(computedValue);

    if (shouldEmitEvent && this._onChangeCallback)
      this._onChangeCallback(computedValue);
  }

  private markAsTouched (): void {
    this._isTouched = true;

    if (this._onTouchedCallback)
      this._onTouchedCallback(true);
  }
}
